import React, { useState, useEffect } from 'react';
import {
    CardHeader,
    CardActions,
    MobileStepper,
    Button,
    CardContent,
    CircularProgress,
    Typography,
    Link,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
//@ts-ignore
import { EmptyState } from '@brightlayer-ui/react-components/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Colors from '@brightlayer-ui/colors';
import ReportIcon from '@mui/icons-material/Report';
// Sub-pages
import { EULA } from './EULA';
import { SetPassword } from './SetPassword';
import { AccountDetails } from './AccountDetails';
import { Complete } from './Complete';
import { BrandedCardContainer } from '../../components/BrandedCardContainer/BrandedCardContainer';
import { useParams, useHistory } from 'react-router-dom';
import { Thunks } from '../../actions';
import { Selectors } from '../../selectors';
import { pushAppRoute } from '../../data/domain/route-manager';
import { CONTACT_EMAIL, CONTACT_PHONENO } from '../../constants';

const useStyles = makeStyles({
    cardContent: {
        flex: '1 1 0px',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export const RegistrationContent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const registration = useSelector(Selectors.Registration.registration);
    // @ts-ignore
    const { code } = useParams();
    const history = useHistory();

    // Stepper state
    const [currentStep, setCurrentStep] = useState(0);
    const [nextEnabled, setNextEnabled] = useState(true);
    const [nextLabel, setNextLabel] = useState<string>(t('ACTIONS.NEXT'));
    const backLabel = t('ACTIONS.BACK');

    // We keep the state of each screen to persist between navigation
    const [eulaScrolled, setEulaScrolled] = useState(false);
    const [eulaAgreed, setEulaAgreed] = useState(false);
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('+1');

    const email = registration.email;
    const organization = registration.organizationName;
    const fname = registration.firstName;
    const lname = registration.lastName;
    let phoneno = registration.phone;
    const existingUser = registration.existingUser;
    useEffect(() => {
        if (!registration.success && code !== undefined) {
            dispatch(Thunks.Registration.validateUserRegistrationRequest(code));
        }

    }, [dispatch, code]); // eslint-disable-line react-hooks/exhaustive-deps

    const eulaScreen = {
        title: t('REGISTRATION.STEPS.LICENSE'),
        content: (
            <EULA
                agree={eulaAgreed}
                onEulaAccept={(val: boolean): void => setEulaAgreed(val)}
                scrolled={eulaScrolled}
                onEulaScrolled={(): void => {
                    setEulaScrolled(true);
                }}
                changeNextEnabled={(enabled: boolean): void => setNextEnabled(enabled)}
                changeNextLabel={(label: string): void => setNextLabel(label)}
                onLoad={(): Function => dispatch(Thunks.Registration.loadEULA(i18n.language))}
            />
        ),
        onNext: (): void => {
            if (fname !== '') {
                setFirstName(fname || '');
                setLastName(lname || '');
                if (phoneno && phoneno.length > 10) {
                    phoneno = phoneno.slice(-10);
                }
                setPhoneNumber(phoneno || '');
            }
        },
    };
    const passwordScreen = {
        title: t('REGISTRATION.STEPS.PASSWORD'),
        content: (
            <SetPassword
                password={password}
                changePassword={(newPass: string): void => setPassword(newPass)}
                changeNextEnabled={(enabled: boolean): void => setNextEnabled(enabled)}
                changeNextLabel={(label: string): void => setNextLabel(label)}
            />
        ),
        onNext: (): void => undefined
    };
    const accountDetailsScreen = {
        title: t('REGISTRATION.STEPS.ACCOUNT_DETAILS'),
        content: (
            <AccountDetails
                first={firstName}
                last={lastName}
                phone={phoneNumber}
                countryCode={countryCode}
                onFirstChanged={(name: string): void => setFirstName(name)}
                onLastChanged={(name: string): void => setLastName(name)}
                onPhoneChanged={(phone: string, countryPhoneCode: string): void => {
                    setPhoneNumber(phone);
                    setCountryCode(countryPhoneCode);
                }}
                changeNextEnabled={(enabled: boolean): void => setNextEnabled(enabled)}
                changeNextLabel={(label: string): void => setNextLabel(label)}
            />
        ),
        onNext: (): void => {
            dispatch(
                Thunks.Registration.completeRegistration(
                    { firstName, lastName, phoneNumber, email, password, countryCode },
                    code || ''
                )
            );
        },
    };
    const completeScreen = {
        title:
            !registration.loading && registration.success
                ? t('REGISTRATION.STEPS.COMPLETE')
                : !registration.loading && !registration.success
                ? t('MESSAGES.ERROR')
                : t('MESSAGES.LOADING'),
        content: (
            <Complete
                changeNextEnabled={(enabled: boolean): void => setNextEnabled(enabled)}
                changeNextLabel={(label: string): void => setNextLabel(label)}
                name={firstName}
                organization={organization}
                email={email}
            />
        ),
        onNext: (): void => {
            pushAppRoute(history, { type: 'Login' });
        },
    };
    const screens = existingUser
        ? [eulaScreen, accountDetailsScreen, completeScreen]
        : [eulaScreen, passwordScreen, accountDetailsScreen, completeScreen];

    const currentScreen = screens[currentStep];

    const next = (): void => {
        if (currentScreen.onNext) {
            currentScreen.onNext();
        }

        if (currentStep + 1 < screens.length) {
            setCurrentStep((currentStep + 1) % screens.length);
        }
    };

    const previous = (): void => {
        setCurrentStep(Math.max(0, currentStep - 1));
    };
    const errorDescription = (errorCode: string): JSX.Element => {
        if (errorCode === '2002') {
            return (
                <Typography variant="subtitle1" style={{ textAlign: 'center' }}>
                    {t(`ERROR_MESSAGES.${registration.errorCode}`)}
                </Typography>
            );
        }
        return (
            <>
                <Typography variant="body1">
                    {t('ERROR_MESSAGES.GENERIC_ERROR')}
                    <Link underline="always" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                    </Link>
                    {t('ERROR_MESSAGES.GENERIC_ERROR_CONTACT_DETAILS')}
                    <Link href={`tel:${CONTACT_PHONENO}`} style={{ textDecoration: 'none' }}>
                        {CONTACT_PHONENO}
                    </Link>
                </Typography>
            </>
        );
    };
    return registration.validationSuccess && !registration.validationloading ? (
        <>
            <CardHeader title={currentScreen.title} data-testid="registration-title" />
            {currentScreen.content}
            <CardActions style={{ padding: 0 }}>
                <MobileStepper
                    variant="dots"
                    steps={screens.length}
                    position="static"
                    activeStep={currentStep}
                    style={{ flex: '1 1 0px', background: 'transparent' }}
                    nextButton={
                        <Button
                            style={{ minWidth: 120 }}
                            disabled={!nextEnabled}
                            variant={'contained'}
                            color={'primary'}
                            onClick={next}
                            data-testid="register-next-button"
                        >
                            {nextLabel}
                        </Button>
                    }
                    backButton={
                        <Button
                            disabled={currentStep < 1}
                            style={{
                                minWidth: 120,
                                visibility: currentStep < 1 ? 'hidden' : 'visible',
                            }}
                            variant="outlined"
                            color={'primary'}
                            onClick={previous}
                            data-testid="register-back-button"
                        >
                            {backLabel}
                        </Button>
                    }
                />
            </CardActions>
        </>
    ) : !registration.validationloading && !registration.validationSuccess ? (
        <>
            <CardContent className={classes.cardContent}>
                <EmptyState
                    title={t('MESSAGES.FAILURE')}
                    actions={errorDescription(registration.errorCode)}
                    icon={
                        <div style={{ fontSize: 64 }}>
                            <ReportIcon id={'fail-icon'} fontSize={'inherit'} htmlColor={Colors.red[500]} />
                        </div>
                    }
                />
            </CardContent>
        </>
    ) : (
        <>
            <CardContent className={classes.cardContent}>
                <CircularProgress id={'progress-spinner'} />
            </CardContent>
        </>
    );
};

export const Registration: React.FC = () => (
    <BrandedCardContainer>
        <RegistrationContent />
    </BrandedCardContainer>
);
